import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Loader from '../components/Loader';
import { useT } from '../i18n';
import './AdminUsersPage.css'; // Reuse the same CSS

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const AdminTokensPage = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newToken, setNewToken] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  // Invite details. The API emails the invitation when an email is given and stores
  // name/plan on the token so the wizard and the account start with them.
  const [invite, setInvite] = useState({ userEmail: '', userName: '', subscriptionPlan: 'basic' });
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invite.userEmail.trim());
  // The admin-password prompt is gone. It existed only because there was no role
  // system — a shared password was the sole way to protect this page. Access is
  // now users.role, enforced server-side on every request, so asking a signed-in
  // owner to re-enter a password guarded nothing and simply added a second login.
  useEffect(() => {
    fetchTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTokens = async () => {
    setLoading(true);
    try {
      // Admin access is now the signed-in user's role, not a shared password.
      // The server dropped ADMIN_TOKEN_PASSWORD (default 'admin123' was live).
      const jwt = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/registration-tokens`, {
        headers: { Authorization: `Bearer ${jwt}` }
      });

      setTokens(response.data);
    } catch (err) {
      console.error('Error fetching tokens:', err);
      
      if (err.response?.status === 401) {
        // The JWT expired. Send them to sign in again — there is no longer a
        // password prompt to fall back to, and there should not be.
        setError(t('admt.sessionExpired'));
        setTimeout(() => navigate('/'), 1500);
      } else if (err.response?.status === 403) {
        setError(t('admt.noAdminAccess'));
      } else {
        setError(err.response?.data?.message || t('admt.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const generateToken = async () => {
    setGenerating(true);
    setError('');
    setNewToken(null);

    try {
      // Admin access is now the signed-in user's role, not a shared password.
      // The server dropped ADMIN_TOKEN_PASSWORD (default 'admin123' was live).
      const jwt = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/registration-tokens/generate`,
        {
          expiresInDays: Number(expiresInDays),
          userEmail: invite.userEmail.trim() || undefined,
          userName: invite.userName.trim() || undefined,
          subscriptionPlan: invite.subscriptionPlan || undefined
        },
        { headers: { Authorization: `Bearer ${jwt}` } }
      );

      setNewToken(response.data);
      setSuccessMessage(response.data.emailed ? t('admt.inviteSent', { email: invite.userEmail.trim() }) : t('admt.tokenGenerated'));
      setInvite((v) => ({ ...v, userEmail: '', userName: '' }));
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchTokens();
    } catch (err) {
      console.error('Error generating token:', err);
      
      if (err.response?.status === 401) {
        // The JWT expired. Send them to sign in again — there is no longer a
        // password prompt to fall back to, and there should not be.
        setError(t('admt.sessionExpired'));
        setTimeout(() => navigate('/'), 1500);
      } else if (err.response?.status === 403) {
        setError(t('admt.noAdminAccess'));
      } else {
        setError(err.response?.data?.message || t('admt.generateFailed'));
      }
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccessMessage(t('admt.copied'));
      setTimeout(() => setSuccessMessage(''), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (token) => {
    if (token.used) {
      return <span className="badge badge-info">{t('admt.used')}</span>;
    }
    if (new Date(token.expires_at) < new Date()) {
      return <span className="badge badge-error">{t('admt.expired')}</span>;
    }
    return <span className="badge badge-success">{t('admt.active')}</span>;
  };

  // Token statistics
  const tokenStats = {
    total: tokens.length,
    active: tokens.filter(t => !t.used && new Date(t.expires_at) >= new Date()).length,
    used: tokens.filter(t => t.used).length,
    expired: tokens.filter(t => !t.used && new Date(t.expires_at) < new Date()).length
  };

  const isBusy = loading || generating;

  return (
    <div className="admin-users-page busy-wrapper">
      {isBusy && <Loader fullscreen={false} />}
      
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-title-section">
            <h1 className="page-title">{t('admt.pageTitle')}</h1>
            <p className="page-subtitle">{t('admt.pageSubtitle')}</p>
          </div>
          <button 
            className="btn-back"
            onClick={() => navigate('/admin/users')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            {t('admt.backToUsers')}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && <div className="alert alert-error">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{tokenStats.total}</span>
            <span className="stat-label">{t('admt.statTotal')}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3fffd8 0%, #2dd4bf 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{tokenStats.active}</span>
            <span className="stat-label">{t('admt.statActive')}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{tokenStats.used}</span>
            <span className="stat-label">{t('admt.used')}</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div className="stat-content">
            <span className="stat-value">{tokenStats.expired}</span>
            <span className="stat-label">{t('admt.expired')}</span>
          </div>
        </div>
      </div>

      {/* Generate Token Section */}
      <div className="content-card">
        <div className="card-header">
          <h2 className="card-title">{t('admt.generateNewToken')}</h2>
        </div>
        <div className="card-body">
          <div className="generate-form">
            <div className="form-row" style={{ flexWrap: 'wrap', gap: 12 }}>
              <div className="form-group" style={{ minWidth: 260 }}>
                <label htmlFor="inviteEmail">{t('admt.inviteEmailLabel')}</label>
                <input
                  type="email"
                  id="inviteEmail"
                  className="form-input"
                  placeholder={t('admt.inviteEmailPlaceholder')}
                  value={invite.userEmail}
                  onChange={(e) => setInvite((v) => ({ ...v, userEmail: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ minWidth: 200 }}>
                <label htmlFor="inviteName">{t('admt.inviteNameLabel')}</label>
                <input
                  type="text"
                  id="inviteName"
                  className="form-input"
                  placeholder={t('admt.inviteNamePlaceholder')}
                  value={invite.userName}
                  onChange={(e) => setInvite((v) => ({ ...v, userName: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="invitePlan">{t('admt.planLabel')}</label>
                <select
                  id="invitePlan"
                  className="form-input"
                  value={invite.subscriptionPlan}
                  onChange={(e) => setInvite((v) => ({ ...v, subscriptionPlan: e.target.value }))}
                >
                  <option value="basic">{t('admt.planBasic')}</option>
                  <option value="advanced">{t('admt.planAdvanced')}</option>
                  <option value="corporate">{t('admt.planCorporate')}</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="expiresInDays">{t('admt.expiresInDaysLabel')}</label>
                <input
                  type="number"
                  id="expiresInDays"
                  className="form-input"
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                  min="1"
                  max="365"
                  style={{ width: '120px' }}
                />
              </div>
              <button
                onClick={generateToken}
                disabled={generating || (invite.userEmail.trim() !== '' && !emailOk)}
                className="btn-primary"
                title={invite.userEmail.trim() ? t('admt.generateWithEmailTitle') : t('admt.generateOnlyTitle')}
              >
                {generating ? t('admt.sending') : (invite.userEmail.trim() ? t('admt.sendInvite') : t('admt.generateToken'))}
              </button>
            </div>
          </div>

          {/* New Token Display */}
          {newToken && (
            <div className="new-token-box">
              <h3>{t('admt.newTokenHeading')}</h3>
              {/* The API now emails the invite. Say plainly whether that worked:
                  a silent failure would leave the admin believing the customer
                  had been contacted when they had not. */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
                padding: '10px 12px', borderRadius: 8, fontSize: 13,
                background: newToken.emailed ? '#e9fbf4' : '#fff5e6',
                color: newToken.emailed ? '#06382f' : '#7a4d00',
                border: `1px solid ${newToken.emailed ? '#b6f0dc' : '#f5d9a8'}`
              }}>
                {newToken.emailed ? (
                  <span>✓ {t('admt.inviteSentToPrefix')} <b>{newToken.userEmail}</b>. {t('admt.inviteSentNoManual')}</span>
                ) : (
                  <span>
                    ⚠ {t('admt.invite')} <b>{t('admt.notSent')}</b>{newToken.emailError ? ` (${newToken.emailError})` : ''}.{' '}
                    {t('admt.copyLinkManually')}
                  </span>
                )}
              </div>
              <div className="token-fields">
                <div className="token-field">
                  <label>{t('admt.registrationLinkLabel')}</label>
                  <div className="token-input-group">
                    <input
                      type="text"
                      value={newToken.registrationUrl}
                      readOnly
                      className="form-input token-readonly"
                    />
                    <button
                      onClick={() => copyToClipboard(newToken.registrationUrl)}
                      className="btn-copy"
                    >
                      {t('admt.copy')}
                    </button>
                  </div>
                </div>
                <div className="token-field">
                  <label>Token:</label>
                  <div className="token-input-group">
                    <input
                      type="text"
                      value={newToken.token}
                      readOnly
                      className="form-input token-readonly"
                    />
                    <button
                      onClick={() => copyToClipboard(newToken.token)}
                      className="btn-copy"
                    >
                      {t('admt.copy')}
                    </button>
                  </div>
                </div>
                <p className="token-expires-info">
                  {t('admt.expiresOn')} <strong>{formatDate(newToken.expiresAt)}</strong>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tokens Table */}
      <div className="content-card">
        <div className="card-header">
          <h2 className="card-title">{t('admt.existingTokens')}</h2>
          <span className="card-count">{t('admt.tokenCount', { count: tokens.length })}</span>
        </div>
        <div className="card-body">
          {tokens.length === 0 ? (
            <div className="empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              <p>{t('admt.emptyTitle')}</p>
              <span>{t('admt.emptyHint')}</span>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('admt.thInviteFor')}</th>
                    <th>Token</th>
                    <th>{t('admt.thStatus')}</th>
                    <th>{t('admt.thCreatedAt')}</th>
                    <th>{t('admt.thExpiresAt')}</th>
                    <th>{t('admt.thUsedAt')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((token) => (
                    <tr key={token.id}>
                      {/*
                        * Who the invite went to. A support question always starts
                        * from the person, never from the UUID.
                        */}
                      <td>
                        {token.user_email ? (
                          <div className="token-recipient">
                            {token.user_name ? <b>{token.user_name}</b> : null}
                            <span>{token.user_email}</span>
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>
                        <code className="token-code">{token.token.substring(0, 20)}...</code>
                      </td>
                      <td>{getStatusBadge(token)}</td>
                      <td>{formatDate(token.created_at)}</td>
                      <td>{formatDate(token.expires_at)}</td>
                      <td>{token.used_at ? formatDate(token.used_at) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTokensPage;
